import type { ReactNode } from 'react';
import { INK, INK_FAINT, INK_SOFT } from '../student/home/ShapesArt';

/*
 * The kit of «Форми 2» for every page (chosen 28.10, tag `design/v4`; his «распространять по всей
 * системе»). A page stands on the warm paper with no panel: its title right on the paper (big, light,
 * Onest), then white blocks without shadows (radius 28). The parts made on the student's home are
 * gathered here: the inks and gradients, the block's head, the two kinds of buttons (↗ into a section,
 * a pill with its icon in a ring), the marks (soft rectangles, never pills), the pictures in one line.
 */

export {
  DARK,
  DEEP,
  GRAD,
  type Grad,
  INK,
  INK_FAINT,
  INK_SOFT,
  LINE,
  PAPER,
  SUBJECT_GRAD,
  grad,
} from '../student/home/ShapesArt';
export {
  Chip,
  Head,
  OpenArrow,
  PillIcon,
  pillClass,
  ringButton,
  ringSmall,
  useDismiss,
} from '../student/home/ShapesDay';
export { EventArt, SubjectArt, TaskArt } from '../student/home/LineArt';

/** A page's title on the paper: the name big and light, a note beside it, a line under it, the actions. */
export function PageHead({
  title,
  note,
  sub,
  children,
}: {
  title: string;
  /** beside the title, faint: the month, a count */
  note?: string;
  /** under the title: one line more */
  sub?: ReactNode;
  /** on the right: the page's buttons */
  children?: ReactNode;
}) {
  return (
    // on a phone the buttons go under the title, so the title is never squeezed
    <header className="flex min-w-0 flex-col gap-3 pt-1 pb-1 sm:flex-row sm:flex-wrap sm:items-end sm:gap-x-6">
      <div className="min-w-0 sm:flex-1">
        <h1
          className="flex min-w-0 flex-wrap items-baseline gap-x-4 font-display text-[40px] leading-[48px] font-light tracking-[-0.04em] md:text-[52px] md:leading-[60px]"
          style={{ color: INK }}
        >
          <span className="min-w-0">{title}</span>
          {note && (
            <span
              className="font-sans text-[15px] leading-6 font-normal tracking-normal"
              style={{ color: INK_FAINT }}
            >
              {note}
            </span>
          )}
        </h1>
        {sub && (
          <div className="mt-1 text-[14px]" style={{ color: INK_SOFT }}>
            {sub}
          </div>
        )}
      </div>
      {children && <div className="flex shrink-0 flex-wrap items-center gap-2">{children}</div>}
    </header>
  );
}

/** A white block of a page: no shadow, radius 28, the inside as on the home. */
export function Block({ className = '', children }: { className?: string; children: ReactNode }) {
  return (
    <section className={`flex min-w-0 flex-col rounded-[28px] bg-white p-5 md:p-6 ${className}`}>
      {children}
    </section>
  );
}

/**
 * A choice of a few (the terms, the views): the options on a soft track of the paper a shade darker,
 * the chosen one white — as the open section of the menu.
 */
export function Tabs<T extends string>({
  value,
  options,
  label,
  onChange,
}: {
  value: T | undefined;
  options: ReadonlyArray<{ id: T; label: string }>;
  label: string;
  onChange: (id: T) => void;
}) {
  return (
    <div
      role="radiogroup"
      aria-label={label}
      className="flex rounded-full p-1"
      style={{ backgroundColor: '#EBE7DF' }}
    >
      {options.map((o) => {
        const on = o.id === value;
        return (
          <button
            key={o.id}
            type="button"
            role="radio"
            aria-checked={on}
            onClick={() => onChange(o.id)}
            className="h-9 rounded-full px-4 text-[14px] font-medium whitespace-nowrap transition-colors"
            style={on ? { backgroundColor: '#FFFFFF', color: INK } : { color: INK_SOFT }}
          >
            {o.label}
          </button>
        );
      })}
    </div>
  );
}
